//External
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
//Internal
import IconClock from "../icons/icons-clock";
import IconChevronDown from "../icons/icons-chevron-down";

/*******************
@Purpose : Custom time dropdown - trigger shows "10:00 AM", panel lists times
           as two columns (hour / AM-PM) with a divider between the AM and PM
           groups, replacing the native <select>. Open state is controlled by
           the parent search bar so it can stay mutually exclusive with the
           calendar, location, and the other time dropdown. The `mobile` flag
           swaps the leading clock icon (desktop hero pill) for a trailing
           chevron (mobile calendar popup), per the two distinct designs.
@Parameter : {value, onChange, options, isOpen, onToggle, onClose, mobile}
@Author : INIC
******************/
function TimeDropdown({ value, onChange, options, isOpen, onToggle, onClose, mobile = false }) {
  const [placement, setPlacement] = useState("below");
  const [offsetStyle, setOffsetStyle] = useState(null);
  const containerRef = useRef();

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  // Open below the field when the search bar sits in the upper half of the
  // page, above it when the search bar sits in the lower half - decided once
  // when the dropdown opens. The panel is positioned relative to this small
  // trigger (.time-dropdown), which sits vertically centered inside the much
  // taller search bar pill - so a plain "100% + gap" offset only clears the
  // trigger itself and ends up overlapping the pill above/below it. Instead
  // we measure the actual pill and offset enough to clear *that*, plus a 4px
  // gap.
  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const gap = 4;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const pillRect = containerRef.current
      .closest(".reservation-search-bar")
      ?.getBoundingClientRect();
    const isInLowerHalf = triggerRect.top > window.innerHeight / 2;

    setPlacement(isInLowerHalf ? "above" : "below");

    if (!pillRect) {
      setOffsetStyle(null);
    } else if (isInLowerHalf) {
      setOffsetStyle({ bottom: triggerRect.bottom - pillRect.top + gap, top: "auto" });
    } else {
      setOffsetStyle({ top: pillRect.bottom - triggerRect.top + gap, bottom: "auto" });
    }
  }, [isOpen]);

  const selectedOption = options.find((option) => `${option.id}:00` === value);
  const selectedLabel = selectedOption?.label || value;

  return (
    <div className="time-dropdown" ref={containerRef}>
      <button
        type="button"
        className="time-dropdown-trigger"
        onClick={onToggle}
      >
        {mobile ? (
          <>
            <span className="time-dropdown-trigger-value">{selectedLabel}</span>
            <IconChevronDown className={`reservation-chevron ${isOpen ? "is-open" : ""}`} />
          </>
        ) : (
          <>
            <IconClock className="reservation-field-icon" />
            <span className="time-dropdown-trigger-value">{selectedLabel}</span>
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={`time-dropdown-panel time-dropdown-panel--${placement}`}
          style={offsetStyle || undefined}
        >
          {options.map((option) => {
            const [time, period] = option.label.split(" ");

            return (
              <button
                key={option.id}
                type="button"
                className={`time-dropdown-row ${
                  `${option.id}:00` === value ? "is-selected" : ""
                } ${option.disabled ? "is-disabled" : ""}`}
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  onChange(`${option.id}:00`);
                  onClose();
                }}
              >
                <span>{time}</span>
                <span>{period}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TimeDropdown;
